import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryAttachments } from '@/components/DiaryAttachments';
import { DiaryText } from '@/components/DiaryTextList/DiaryText';
import { HomeTaskCard } from '@/components/HomeTaskCard';
import { HomeTaskModal } from '@/components/HomeTaskModal';
import { LessonVideoMeeting } from '@/components/LessonVideoMeeting';
import { useOpenThirdPartyEvent } from '@/hooks/useOpenThirdPartyEvent';
import { HomeTaskType, type AttachmentsDisplay } from '@/types/components';
import type { DiaryHomeTask, DiaryThirdPartyEvent } from '@/types/diary';
import type { VideoMeetingResponse } from '@/types/video';
import { thirdPartyTaskTypes } from '@/utils/homeTask';

interface Props {
  homeTasks: DiaryHomeTask[];
  thirdPartyEvents?: DiaryThirdPartyEvent[];
  videoMeetings?: VideoMeetingResponse[];
  attachmentsDisplay?: AttachmentsDisplay;
  isFullText?: boolean;
  className?: string;
  onOpenHomeTask?: (homeTaskId: number) => void;
}

export const DiaryHomeTasks: React.FC<Props> = ({
  homeTasks,
  thirdPartyEvents = [],
  videoMeetings = [],
  attachmentsDisplay,
  isFullText,
  className,
  onOpenHomeTask,
}) => {
  const { t } = useTranslation();
  const [openHomeTaskId, setOpenHomeTaskId] = useState<number | null>(null);
  const openThirdPartyEvent = useOpenThirdPartyEvent();
  const plainTasks = homeTasks.filter((task) => !task.areAnswersEnabled);
  const answerTasks = homeTasks.filter((task) => task.areAnswersEnabled);
  const taskAttachments = plainTasks.flatMap(({ attachments }) => attachments);
  const openHomeTask = onOpenHomeTask ?? setOpenHomeTaskId;
  const [videoMeeting] = videoMeetings;

  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className ?? ''}`}>
      {videoMeeting && <LessonVideoMeeting meeting={videoMeeting} />}

      {plainTasks.map((task, index) => (
        <div
          key={task.id}
          className={`flex ${index > 0 ? 'border-t border-divider pt-2' : ''}`}
        >
          {!!task.text && (
            <DiaryText text={task.text} isFullText={isFullText} />
          )}
          {!task.text && (
            <span className="text-body-14 flex-1">
              {t('diary.emptyDescription')}
            </span>
          )}
        </div>
      ))}

      {answerTasks.map((task) => (
        <HomeTaskCard
          key={task.id}
          type={HomeTaskType.EKlase}
          text={task.text}
          hasStudentAnswer={task.hasStudentAnswer}
          onClick={() => openHomeTask(task.id)}
        />
      ))}

      {thirdPartyEvents.map((event) => (
        <HomeTaskCard
          key={event.url}
          type={thirdPartyTaskTypes[event.applicationId] ?? HomeTaskType.EKlase}
          text={event.name}
          isDeleted={event.isDeleted}
          onClick={() => openThirdPartyEvent(event)}
        />
      ))}

      <DiaryAttachments
        attachments={taskAttachments}
        display={attachmentsDisplay}
      />

      {openHomeTaskId !== null && (
        <HomeTaskModal
          homeTaskId={openHomeTaskId}
          onClose={() => setOpenHomeTaskId(null)}
        />
      )}
    </div>
  );
};
